import React, { useEffect, useState, useRef, Fragment } from "react";
import { engineInitialSelector } from "../../constants/trendGraph/engine";
import { VESSEL_TYPE } from "../../model/slice/vesselSlice";
import { SEP_CHART_TYPE } from "../../constants/trendGraph/sepLines";
import { ENGINE_CHART_TYPE } from "../../constants/trendGraph/enginesLine.js";
import { useComparisonDataFetch } from "./hooks/useComparisonDataFetch";
import { IconButton, CircularProgress } from "@mui/material";
import Grid from "@mui/material/Grid2";
import MinimizeIcon from "../trendPage/icon/minimizeIcon.jsx";
import MaximizeIcon from "../trendPage/icon/maximizeIcon.jsx";
import {ComparisonGraphSelector} from "./comparisonGraphSelector.jsx";
import { Chart } from "../chart/chart.jsx";
import * as PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedVessel } from "../../model/slice/fleetNavigationDataSlice";
import { useNavigate, useLocation } from "react-router-dom";
import { NoData } from "../offlineComponent/noData.jsx";
import { GraphNotSelected } from "../comparisonGraphNotSelectedComponent/comparisonGraphNotSelected.jsx";
import { fetchVessel } from "../../api/vessel.js";
import { ComparisonRightPanel } from "./comparisonRightPanel.jsx";
import { Screenshot } from "../trendPage/commonComponent/screenshot.jsx";
import { CsvDownload } from "../trendPage/commonComponent/csvDownload.jsx";
import { GRAPH_TYPE } from "../../constants/trendGraph/trendGraphType";
import { fetchCharts } from "../../api/charts.js";
import { fetchChannelNames } from "../../api/channel.js";
import { DEFAULT_LINE_COLORS } from "../../constants/trendGraph/linesDefaultColors.js";
import { formatDate } from "../../util/timezone.js";
import { selectedTimezone } from "../../model/slice/timezoneSlice.js";
import { CHANNEL_TYPE } from "../../constants/channel/config";
import { NAVIGATION_BAR_HEIGHT, TAB_BAR_HEIGHT } from "../../constants/layout.js";
import { updateChartLineName } from "../../model/slice/chartSettingsSlice.js";

const PAPER_HEIGHT = `calc(50vh - ${NAVIGATION_BAR_HEIGHT}px - ${TAB_BAR_HEIGHT}px - 20px)`;

const styles = {
  paperHeight: {
    borderRadius: "12px",
  },
  titlePaperPosition: {
    position: "relative",
    borderRadius: "12px 12px 0 0",
  },
  durationControl: {
    margin: "16px 0",
  },
  notSelected: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "calc(100% - 20px)",
    "& p": {
      color: "#ffffff",
      fontSize: "16px",
    },
  },
  burgerMenu: {
    position: "absolute",
    left: "10px",
    top: `12px`,
    padding: "0px",
  },
  burgerMenuIcon: {
    color: "#19b3ab",
  },
  maximize: {
    position: "absolute",
    right: "10px",
    top: `12px`,
    padding: "0px",
  },
  comparisonTopArea: {
    display: "flex",
    justifyContent: "end",
    paddingRight: "24px"
  },
  utilityIconArea: {
    height: "30px",
    display: "flex",
    justifyContent: "end",
    "& span": {
      width: "25px",
    },
  },
  iconButtonContainer: {
    "& p": {
      fontFamily: "Inter",
      fontSize: "16px",
      fontWeight: "500",
      fontStretch: "normal",
      lineHeight: "normal",
      letterSpacing: "normal",
      color: "#19b2ab",
      margin: "0",
    },
    width: "max",
    height: `50px`,
    backgroundColor: "rgba(44, 58, 72, 0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "none",
  },
} 

export const ComparisonGraph = (props) => {
  const {
    number,
    initialVesselId,
    vessels,
    setVessels,
    graphRef,
    typeForComparison,
    setTypeForComparison,
    setSelectedGraphTypeForComparison,
    selectedGraphForComparison,
    setSelectedGraphForComparison,
    graphDataForComparison,
    setGraphDataForComparison,
    isLoadingForComparison,
    setIsLoadingForComparison,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
  } = props;

  const dispatch = useDispatch();
  const [currentSelectedVessel, setCurrentSelectedVessel] = useState(() => {
    if (number === 0) {
      return initialVesselId;
    } else {
      return null;
    }
  });
  const vessel = vessels[number];
  const navigate = useNavigate();
  const location = useLocation();
  const screenshotRef = useRef();
  const isSepVessel = vessel?.vesselType === VESSEL_TYPE.SEP;
  const getGraphTypeByVesselId = (vesselId) => {
    if (vessel?.length == 0) return null;
    if(isSepVessel) return GRAPH_TYPE.SEP;
    if(vessel?.NoME > 0) return GRAPH_TYPE.ENGINE;
    return;
  };

  const initialSelectedGraph = () => {
    if (!currentSelectedVessel) return null;
    if (isSepVessel) return SEP_CHART_TYPE.LEGS;
    if (vessel?.NoME > 0) return ENGINE_CHART_TYPE.ENGINE_LOAD;
    return;
  };

  const initialGraphTypeSelect = () => {
    if (!currentSelectedVessel) return null;
    return getGraphTypeByVesselId(currentSelectedVessel);
  };

  const updateVesselData = async (vessel_id, number) => {
    if (!vessel_id) return;
    const newVesselData = await fetchVessel(vessel_id);
    setVessels(
      vessels.map((element, index) => {
        if (index === number) return newVesselData.vessel;
        return element;
      })
    );
  };

  const [enginePositions, setEnginePositions] = useState(() => engineInitialSelector(vessel?.NoME));
  const [selectedGraph, setSelectedGraph] = useState(() => initialSelectedGraph());
  const [selectedGraphType, setSelectedGraphType] = useState(() => initialGraphTypeSelect());
  const [isMaximize, setIsMaximize] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showGraphUtilityIcon, setShowGraphUtilityIcon] = useState(false);
  const [customCharts, setCustomCharts] = useState(null);
  const [customLines, setCustomLines] = useState(null);
  const [channelNames, setChannelNames] = useState(null);
  const [isFetchingChartInfo, setIsFetchingChartInfo] = useState(false);
  const [graphDataDigital, setGraphDataDigital] = useState([]);
  const timezone = useSelector(selectedTimezone);

  const { isLoading, graphData, graphConfig, digitalData } = useComparisonDataFetch({
    vessel,
    vesselId: currentSelectedVessel,
    selectedGraph,
    engineType: vessel?.MEEngineType,
    position: enginePositions,
    startDate,
    endDate,
    number,
    selectedGraphType,
    customLines,
  });

  const displayGraphName = () => {
    if(selectedGraphType === GRAPH_TYPE.ELECTRIC) {
      return GRAPH_TYPE.ELECTRIC
    }else if(selectedGraphType === GRAPH_TYPE.CUSTOM) {
      return customCharts?.charts?.charts?.find((v) => v.id === selectedGraph)?.name
    }else{
      return selectedGraph
    }
  }

  useEffect(() => {
    dispatch(setSelectedVessel(initialVesselId));
  }, []);

  useEffect(() => {
    dispatch(updateChartLineName(graphConfig));
  }, [graphConfig]);

  useEffect(() => {
    if (
      number === 0 &&
      (currentSelectedVessel === undefined || currentSelectedVessel !== initialVesselId)
    ) {
      setCurrentSelectedVessel(initialVesselId);
      setSelectedGraph(initialSelectedGraph());
    }
  }, [initialVesselId]);

  // to sync No.1 trend graph selected vessel and vessel Selector
  useEffect(() => {
    if (number === 0 && currentSelectedVessel !== initialVesselId) {
      dispatch(setSelectedVessel(currentSelectedVessel));
      const currentLocation = location.pathname.split("/")[1];
      navigate(`/${currentLocation}/${currentSelectedVessel}`);
    }
    updateVesselData(currentSelectedVessel, number);
  }, [currentSelectedVessel]);

  useEffect(() => {
    if (!selectedGraphType) {
      setSelectedGraphType(initialGraphTypeSelect());
    }
    if (!selectedGraph) {
      setSelectedGraph(initialSelectedGraph());
    }

    if (vessel) {
      let nameLists = [];
      if (vessel?.NoDG > 0) {
        nameLists = nameLists.concat([...new Array(vessel?.NoDG)].map((_, i) => `dg${i + 1}`));
      }
      if (vessel?.NoSG > 0) {
        nameLists = nameLists.concat([...new Array(vessel?.NoSG)].map((_, i) => `sg${i + 1}`));
      }
      if (!nameLists.length) {
        return;
      }
    }
  }, [currentSelectedVessel, vessel]);

  useEffect(() => {
    setSelectedGraph((value) => {
      if (isSepVessel) return SEP_CHART_TYPE.LEGS;
      else if (vessel?.vesselType === VESSEL_TYPE.vessel) return ENGINE_CHART_TYPE.ENGINE_LOAD;
      else return null;
    });
    setSelectedGraphType(() => getGraphTypeByVesselId(currentSelectedVessel));
    setEnginePositions(engineInitialSelector(vessel?.NoME) ?? []);
  }, [vessel]);

  useEffect(() => {
    setTypeForComparison([...typeForComparison.map((e, i) => (i === number ? selectedGraphType : e))]);
    setSelectedGraphTypeForComparison([
      ...selectedGraphForComparison.map((e, i) => (i === number ? selectedGraphType : e)),
    ]);
    setSelectedGraphForComparison([
      ...selectedGraphForComparison.map((e, i) => (i === number ? selectedGraph : e)),
    ]);
    setGraphDataForComparison([
      ...graphDataForComparison.map((e, i) => (i === number ? graphData : e)),
    ]);
    setIsLoadingForComparison([
      ...isLoadingForComparison.map((e, i) => (i === number ? isLoading : e)),
    ]);
  }, [isLoading]);

  useEffect(() => {
    const fetchCustomCharts = async () => {
      if(!vessel?.id) return;
      setIsFetchingChartInfo(true);
      const customChartsList = await fetchCharts(vessel?.id);
      const customChartsName = await fetchChannelNames(vessel?.id, 'comparison');
      setCustomCharts(customChartsList)
      setChannelNames(customChartsName);
      setIsFetchingChartInfo(false);
    }
    fetchCustomCharts();
  }, [vessel?.id]);

  useEffect(() => {
    if (selectedGraphType !== GRAPH_TYPE.CUSTOM) {
      setGraphDataDigital([]);
      return;
    }
    const digitalChs = customLines?.filter(line => line?.digitalVar !== null).map(line => line?.key);
    const selectedDigitalData = digitalData.map(data => {
      const digital = Object.keys(data).reduce((acc, key) => {
        if (digitalChs.includes(key)) acc[key] = data[key];
        return acc;
      }, {});
      return Object.assign(digital, { dateTime: data.dateTime })
    });
    setGraphDataDigital(selectedDigitalData);
  }, [customLines, digitalData, selectedGraphType]);

  useEffect(() => {
    if (selectedGraphType !== GRAPH_TYPE.CUSTOM || !customCharts?.charts?.charts?.length) return;
    const lines = customCharts?.charts?.charts?.find((v) => v.id === selectedGraph)?.lines;
    const returnValue = lines
      ?.map((line, idx) => {
        const channel = channelNames?.names.find((ch) => ch?.chNo === line?.chNo);
        return {
          key: `${line?.chNo}`,
          keyName: `${line?.chNo}`,
          name: channel?.chName,
          digitalVar: line?.digitalVar,
          channelType: line?.digitalVar === null ? CHANNEL_TYPE.ANALOG: CHANNEL_TYPE.DIGITAL,
          color: line?.color === '' || !line?.color ? DEFAULT_LINE_COLORS[idx] : line?.color,
          enableVisibilityIcon: "hidden",
          enable: true,
        };
      });
    setCustomLines(returnValue);
  }, [selectedGraph, customCharts]);

  const isNoData = () => {
    if(isLoading){return false;}
    if(!graphData || graphData.length == 0) {return true;}
    return false;
  };

  const getChartName = (selectedGraphType, selectedGraph) => {
    return selectedGraphType === GRAPH_TYPE.CUSTOM
      ? customCharts?.charts?.charts?.find((v) => v.id === selectedGraph)?.name?.replace(/\s/g, "-")
      : selectedGraph;
  }

  const canShowUtilityIcons = selectedGraph && !isLoading && showGraphUtilityIcon && !isNoData();
 
  return (
    <Fragment>
      <Grid item size={{ xs: isMaximize ? 12 : 9 }}>
        <Grid
          sx={styles.paperHeight}
          onMouseEnter={() => setShowGraphUtilityIcon(true)}
          onMouseLeave={() => setShowGraphUtilityIcon(false)}
          style={{
              height: `fit-content`,
          }}
        >
          <Grid
            container
            sx={styles.comparisonGraphHeight}
            style={{
              height: `fit-content`,
            }}
          >
            <Grid
              item
              size={{ xs: 12 }}
              sx={{
                height: `fit-content`,
              }}
              ref={screenshotRef}
            >
              {currentSelectedVessel &&
              ((selectedGraph !== null && !!graphData && !isNoData())) ? 
              (
                <Fragment>
                  <Grid container justifyContent="space-between">
                    <Grid item sx={{color: "white", marginTop : "8px", marginLeft: "30px"}}>
                      {`${vessel?.name} - ${displayGraphName()}  ${formatDate(startDate, timezone, 'YYYY/MM/DD HH:mm')} - ${formatDate(endDate, timezone, 'YYYY/MM/DD HH:mm')}`}
                    </Grid>
                    <Grid
                      item
                      sx={{...styles.utilityIconArea, visibility: canShowUtilityIcons ? "visible" : "hidden"}}
                    >
                      <Screenshot
                        marginRight={"0px"}
                        element={screenshotRef.current}
                        vesselName={vessel?.name}
                        graphType={selectedGraphType}
                        selectedGraph={getChartName(selectedGraphType, selectedGraph)}
                        selectedEngines={enginePositions}
                      />
                      <CsvDownload
                        graphRef={graphRef}
                        vesselId={vessel?.id}
                        graphData={graphData}
                        graphConfig={graphConfig}
                        graphType={selectedGraphType}
                        selectedChart={selectedGraph}
                        selectedEngines={enginePositions}
                        isDownloading={isDownloading}
                        setIsDownloading={setIsDownloading}
                        isNoData={false}
                        isDurationDisable={false}
                        is1SecFetchEnabled={true}
                        isComparison={true}
                        isLoading={isLoading}
                        vesselName={vessel?.name}
                        chartsNameSettings={customLines}
                        customChartsList={customCharts}
                        isSepVessel={isSepVessel}
                        startDate={startDate}
                        endDate={endDate}
                      />
                      <IconButton
                        style={{ width: "25px", height: "25px", top: "1px", margin: "0 57px 0 10px" }}
                        onClick={() => {
                          setIsMaximize((val) => !val);
                        }}
                        size="large">
                        {isMaximize ? (
                          <MinimizeIcon disabled={!graphData} />
                        ) : (
                          <MaximizeIcon disabled={!graphData} />
                        )}
                      </IconButton>
                    </Grid>
                  </Grid>
                  <Chart
                    type={selectedGraphType}
                    height={320}
                    graphData={graphData}
                    graphDataDigital={graphDataDigital}
                    isNoData={isNoData()}
                    vesselId={currentSelectedVessel}
                    vesselType={vessel?.MEEngineType}
                    isGraphDataLoading={isLoading}
                    chartType={selectedGraph}
                    style={{ border: "solid white" }}
                    isComparison={true}
                    comparisonGraphIndex={number}
                    customChartLines={customLines}
                    vessel={vessel}
                  />
                </Fragment>
              ) : (
                !currentSelectedVessel || !selectedGraph
                ? <GraphNotSelected/>
                : (
                  isNoData()
                  ? <NoData/>
                  : <CircularProgress size={80} sx={{position: "relative", left: "50%", top: "50%"}}/>
                )
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {!isMaximize && (
        <Grid item size={{ xs: 3 }}>
          <Grid
            sx={{...styles.paperHeight, height: `calc(${PAPER_HEIGHT} + ${"30"}px)`}}
          >
            <ComparisonRightPanel>
              <ComparisonGraphSelector
                currentVesselInfo={vessel}
                graphType={selectedGraphType}
                currentSelectedVessel={currentSelectedVessel}
                setCurrentSelectedVessel={setCurrentSelectedVessel}
                selectedGraphType={selectedGraphType}
                setSelectedGraphType={setSelectedGraphType}
                selectedGraph={selectedGraph}
                setSelectedGraph={setSelectedGraph}
                enginePositions={enginePositions}
                setEnginePositions={setEnginePositions}
                customCharts={customCharts}
                isFetchingChartInfo={isFetchingChartInfo}
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
              />
            </ComparisonRightPanel>
          </Grid>
        </Grid>
      )}
    </Fragment>
  );
};

ComparisonGraph.propTypes = {
  number: PropTypes.number,
  styles: PropTypes.object,
  initialVesselId: PropTypes.string,
  vessels: PropTypes.arrayOf(PropTypes.object),
  setVessels: PropTypes.func,
  graphRef: PropTypes.any,
  typeForComparison: PropTypes.array,
  setTypeForComparison: PropTypes.func,
  setSelectedGraphTypeForComparison: PropTypes.func,
  selectedGraphForComparison: PropTypes.array,
  setSelectedGraphForComparison: PropTypes.func,
  graphDataForComparison: PropTypes.array,
  setGraphDataForComparison: PropTypes.func,
  isLoadingForComparison: PropTypes.arrayOf(PropTypes.bool),
  setIsLoadingForComparison: PropTypes.func,
  startDate: PropTypes.string,
  setStartDate: PropTypes.func,
  endDate: PropTypes.string,
  setEndDate: PropTypes.func,
};
